import { useCallback, useMemo } from "react";
import {
  ArtifactType,
  GoalState,
  GoalStatus,
  LoggedInUserOrgFragment,
} from "types/graphql-schema";
import { DateRangeEnum, PastOnlyDateRangeEnum } from "types/topicflow";

import { currentOrganizationVar } from "@cache/cache";
import { artifactType } from "@helpers/constants";
import { capitalize, lowerCase, pluralize, titleCase } from "@helpers/string";

const defaultLabels = {
  [`${ArtifactType.ActionItem}`]: "action item",
  [`${ArtifactType.Decision}`]: "decision",
  [`${ArtifactType.Rating}`]: "rating",
  [`${ArtifactType.Document}`]: "document",
  [`${ArtifactType.Goal}`]: "goal",
  [`${ArtifactType.AsanaTask}`]: "Asana task",
  [`${ArtifactType.Kpi}`]: "KPI",
  [`${ArtifactType.Recognition}`]: "recognition",
  team: "team",
  organization: "organization",
  career: "career",
  development: "development",
  "key result": "key result",
  [`${GoalStatus.AtRisk}`]: "at risk",
  [`${GoalStatus.OnTrack}`]: "on track",
  [`${GoalStatus.OffTrack}`]: "off track",
  [`${GoalStatus.None}`]: "none",
  [`${GoalState.Draft}`]: "draft",
  [`${GoalState.Open}`]: "in progress",
  [`${GoalState.Closed}`]: "closed",
  [`${DateRangeEnum.nextMonth}`]: "next month",
  [`${DateRangeEnum.thisMonth}`]: "this month",
  [`${DateRangeEnum.previousMonth}`]: "previous month",
  [`${DateRangeEnum.nextQuarter}`]: "next quarter",
  [`${DateRangeEnum.thisQuarter}`]: "this quarter",
  [`${DateRangeEnum.previousQuarter}`]: "previous quarter",
  [`${PastOnlyDateRangeEnum.previousYear}`]: "previous year",
  oneonone: "1-on-1",
  review: "review",
  competency: "competency",
} as { [key: string]: string };

interface LabelTransformers {
  capitalize?: boolean;
  pluralize?: boolean | number;
  titleCase?: boolean;
}

export type UseLabelType = (
  sentence: keyof typeof defaultLabels,
  transformers?: LabelTransformers
) => string;

const labelDefinitions = (currentOrganization: LoggedInUserOrgFragment) => ({
  ...defaultLabels,
  [`${artifactType.goal}`]: lowerCase(
    currentOrganization?.goalLabel || defaultLabels[`${artifactType.goal}`]
  ),
  "key result": lowerCase(
    currentOrganization?.keyResultLabel || defaultLabels["key result"]
  ),
  team: lowerCase(currentOrganization?.teamLabel || defaultLabels["team"]),
  organization: lowerCase(
    currentOrganization?.orgLabel || defaultLabels["organization"]
  ),
  recognition: lowerCase(
    currentOrganization?.recognitionLabel || defaultLabels["recognition"]
  ),
  oneonone: lowerCase(
    currentOrganization?.oneononeLabel || defaultLabels["oneonone"]
  ),
  review: lowerCase(
    currentOrganization?.reviewLabel || defaultLabels["review"]
  ),
  competency: lowerCase(
    currentOrganization?.competencyLabel || defaultLabels["competency"]
  ),
});

const getLabelFunc =
  (labels: any) =>
  (
    sentence: keyof typeof defaultLabels,
    transformers: LabelTransformers = {}
  ) => {
    const defaultTransformers = {
      capitalize: false,
      pluralize: false,
      titleCase: false,
    };

    const label = lowerCase(labels[`${sentence}`] || String(sentence));
    return Object.keys({ ...defaultTransformers, ...transformers }).reduce(
      (memo: string, transformerKey: string) => {
        if (
          transformerKey === "pluralize" &&
          transformers[transformerKey] === true
        ) {
          return pluralize(memo, 2);
        }
        if (
          transformerKey === "pluralize" &&
          typeof transformers[transformerKey] === "number"
        ) {
          return pluralize(memo, transformers[transformerKey] as number);
        }
        if (transformerKey === "titleCase" && transformers[transformerKey]) {
          return titleCase(memo);
        }
        if (transformerKey === "capitalize" && transformers[transformerKey]) {
          return capitalize(memo);
        }
        return memo;
      },
      label
    );
  };

export const getLabel = (): UseLabelType => {
  const currentOrganization = currentOrganizationVar();
  const labels = labelDefinitions(currentOrganization);
  return getLabelFunc(labels);
};

const useLabel = (): UseLabelType => {
  const org = useMemo(() => currentOrganizationVar(), []);
  const labels = useMemo(() => labelDefinitions(org), [org]);
  return useCallback(getLabelFunc(labels), [labels]);
};

export default useLabel;
